import React, { useState, useEffect, useRef } from "react";
import { CSVLink } from "react-csv";
import "./ManageLocksmith.css";
import api from "../../api/api";

const ManageLocksmith = () => {
  const [locksmiths, setLocksmiths] = useState([]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [updatingCommission, setUpdatingCommission] = useState(null);
  const [successCommission, setSuccessCommission] = useState(null);
  const [updatingCommissionAmount, setUpdatingCommissionAmount] = useState(null);
  const [successCommissionAmount, setSuccessCommissionAmount] = useState(null);
  const [updatingLockieReward, setUpdatingLockieReward] = useState(null);
  const [successLockieReward, setSuccessLockieReward] = useState(null);
  const csvLinkRef = useRef(null);

  const fetchLocksmiths = async (searchQuery = "", sort = "") => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setError("Unauthorized access. Please login as an admin.");
      return;
    }

    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (sort) params.ordering = sort === "asc" ? "user__date_joined" : "-user__date_joined";

      const response = await api.get("/api/locksmiths/", {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setLocksmiths(response.data);
    } catch (err) {
      setError("Failed to fetch locksmiths.");
    }
  };

  useEffect(() => {
    fetchLocksmiths();
  }, []);

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearch(searchValue);
    fetchLocksmiths(searchValue, sortOrder);
  };

  const handleSort = (order) => {
    setSortOrder(order);
    fetchLocksmiths(search, order);
  };

  const handleApprove = async (id) => {
    const token = localStorage.getItem("accessToken");
    if (!token) return setError("Unauthorized access. Please login.");

    try {
      await api.put(
        `/api/locksmiths/${id}/verify_locksmith/`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setLocksmiths((prev) =>
        prev.map((l) => (l.id === id ? { ...l, is_approved: true } : l))
      );
    } catch (err) {
      setError(`Failed to approve locksmith with ID: ${id}`);
    }
  };

  const handleReject = async (id) => {
    const token = localStorage.getItem("accessToken");
    if (!token) return setError("Unauthorized access. Please login.");

    try {
      await api.put(
        `/api/locksmiths/${id}/reject_locksmith/`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setLocksmiths((prev) =>
        prev.map((l) => (l.id === id ? { ...l, is_approved: false } : l))
      );
    } catch (err) {
      setError(`Failed to reject locksmith with ID: ${id}`);
    }
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem("accessToken");
    if (!token) return setError("Unauthorized access. Please login.");

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this locksmith? This action cannot be undone."
    );
    if (!confirmDelete) return;

    try {
      await api.delete(`/api/locksmiths/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setLocksmiths((prev) => prev.filter((l) => l.id !== id));
    } catch (err) {
      setError(`Failed to delete locksmith with ID: ${id}`);
    }
  };

  const handleCommissionChange = async (id, value) => {
    const token = localStorage.getItem("accessToken");
    if (!token) return setError("Unauthorized access. Please login.");

    // Validate percentage value
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0 || numValue > 100) {
      setError("Commission percentage must be between 0 and 100");
      return;
    }

    // Set updating state
    setUpdatingCommission(id);
    setSuccessCommission(null);

    try {
      await api.put(
        `/api/locksmiths/${id}/update_commission_percentage/`,
        { commission_percentage: numValue },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setLocksmiths((prev) =>
        prev.map((l) => (l.id === id ? { ...l, commission_percentage: numValue } : l))
      );

      // Set success state
      setUpdatingCommission(null);
      setSuccessCommission(id);

      // Clear success state after 3 seconds
      setTimeout(() => {
        setSuccessCommission((current) => (current === id ? null : current));
      }, 3000);
    } catch (err) {
      setUpdatingCommission(null);
      setError(`Failed to update commission percentage for locksmith ID: ${id}`);
    }
  };

  const handleCommissionAmountChange = async (id, value) => {
    const token = localStorage.getItem("accessToken");
    if (!token) return setError("Unauthorized access. Please login.");

    // Handle empty string, null, or 0 - all are valid
    let numValue = null;
    if (value !== "" && value !== null && value !== undefined) {
      numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) {
        setError("Commission amount cannot be negative");
        return;
      }
      // numValue is already set correctly (0 or positive number)
    }

    // Set updating state
    setUpdatingCommissionAmount(id);
    setSuccessCommissionAmount(null);

    try {
      await api.put(
        `/api/locksmiths/${id}/update_commission_amount/`,
        { commission_amount: numValue },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setLocksmiths((prev) =>
        prev.map((l) => (l.id === id ? { ...l, commission_amount: numValue } : l))
      );

      // Set success state
      setUpdatingCommissionAmount(null);
      setSuccessCommissionAmount(id);

      // Clear success state after 3 seconds
      setTimeout(() => {
        setSuccessCommissionAmount((current) => (current === id ? null : current));
      }, 3000);
    } catch (err) {
      setUpdatingCommissionAmount(null);
      setError(`Failed to update commission amount for locksmith ID: ${id}`);
    }
  };

  //lockie reward handler
  const handleLockieRewardChange = async (id, value) => {
    const token = localStorage.getItem("accessToken");
    if (!token) return setError("Unauthorized access. Please login.");
  
    // Empty or "0" = clear (no reward). Backend accepts null or "0".
    let payload = null;
    if (value !== "" && value !== null && value !== undefined) {
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0 || numValue > 100) {
        setError("Lockie Reward % must be between 0 and 100");
        return;
      }
      payload = numValue === 0 ? null : String(numValue); // backend expects string or null
    }
  
    setUpdatingLockieReward(id);
    setSuccessLockieReward(null);
  
    try {
      await api.put(
        `/api/locksmiths/${id}/update_lockie_reward_percentage/`,
        { lockie_reward_percentage: payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );
  
      setLocksmiths((prev) =>
        prev.map((l) =>
          l.id === id
            ? { ...l, lockie_reward_percentage: payload === null ? null : parseFloat(payload) }
            : l
        )
      );
  
      setUpdatingLockieReward(null);
      setSuccessLockieReward(id);
      setTimeout(() => {
        setSuccessLockieReward((current) => (current === id ? null : current));
      }, 3000);
    } catch (err) {
      setUpdatingLockieReward(null);
      setError(`Failed to update Lockie Reward % for locksmith ID: ${id}`);
    }
  };


  const csvHeaders = [
    { label: "Username", key: "user.username" },
    { label: "Email", key: "user.email" },
    { label: "Contact", key: "contact_number" },
    { label: "Commission %", key: "commission_percentage" },
    { label: "Lockie Reward %", key: "lockie_reward_percentage" },
    { label: "Address", key: "address" },
    { label: "Date Joined", key: "user.date_joined" },
  ];

  return (
    <div className="locksmith-container">
      <div className="locksmith-header">
        <h2>Locksmith Details</h2>
        <div className="locksmith-controls">
          <input
            type="text"
            placeholder="Search by username, email, or contact number"
            value={search}
            onChange={handleSearch}
            className="search-bar"
          />
          <div className="sort-buttons">
            <button
              className={`sort-btn ${sortOrder === "asc" ? "active" : ""}`}
              onClick={() => handleSort("asc")}
            >
              Sort Oldest First
            </button>
            <button
              className={`sort-btn ${sortOrder === "desc" ? "active" : ""}`}
              onClick={() => handleSort("desc")}
            >
              Sort Newest First
            </button>
          </div>
          <CSVLink
            data={locksmiths}
            headers={csvHeaders}
            filename="locksmiths_export.csv"
            className="export-btn"
            ref={csvLinkRef}
          >
            Export to CSV
          </CSVLink>
        </div>
      </div>

      <div className="table-container">
        <table className="locksmith-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Username</th>
              <th>Email</th>
              <th>Address</th>
              <th>Contact No</th>
              <th>PCC File</th>
              <th>Licence File</th>
              <th>Photo</th>
              <th>Service Area</th>
              <th>Status</th>
              <th>Commission %</th>
              <th>Commission Amount</th>
              <th>Lockie Reward %</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {locksmiths.map((l) => (
              <tr key={l.id}>
                <td>{l.id}</td>
                <td>{l.user.username}</td>
                <td>{l.user.email}</td>
                <td>{l.address}</td>
                <td>{l.contact_number}</td>
                <td>
                  {l.pcc_file ? (
                    <a href={l.pcc_file} download>
                      Download
                    </a>
                  ) : (
                    "No File"
                  )}
                </td>
                <td>
                  {l.license_file ? (
                    <a href={l.license_file} download>
                      Download
                    </a>
                  ) : (
                    "No File"
                  )}
                </td>
                <td>
                  <img src={l.photo} alt="Locksmith" className="photo" />
                </td>
                <td>{l.service_area || "N/A"}</td>
                <td>{l.is_approved ? "Approved" : "Pending"}</td>
                <td>
                  <div className="commission-input-wrapper">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      className={`commission-input ${
                        updatingCommission === l.id ? "updating" : ""
                      } ${successCommission === l.id ? "success" : ""}`}
                      value={l.commission_percentage || ""}
                      placeholder="0"
                      onChange={(e) => {
                        const value = e.target.value;
                        setLocksmiths((prev) =>
                          prev.map((item) =>
                            item.id === l.id
                              ? { ...item, commission_percentage: value }
                              : item
                          )
                        );
                      }}
                      onBlur={(e) => handleCommissionChange(l.id, e.target.value)}
                    />
                    <span className="percent-symbol">%</span>
                  </div>
                </td>
                <td>
                  <div className="commission-input-wrapper">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={`commission-input ${
                        updatingCommissionAmount === l.id ? "updating" : ""
                      } ${successCommissionAmount === l.id ? "success" : ""}`}
                      value={l.commission_amount === null || l.commission_amount === undefined ? "" : l.commission_amount}
                      placeholder="0"
                      onChange={(e) => {
                        const value = e.target.value;
                        setLocksmiths((prev) =>
                          prev.map((item) =>
                            item.id === l.id
                              ? { ...item, commission_amount: value === "" ? "" : value }
                              : item
                          )
                        );
                      }}
                      onBlur={(e) => handleCommissionAmountChange(l.id, e.target.value)}
                    />
                  </div>
                </td>

                {/*lockie reward*/}
                <td>
  <div className="commission-input-wrapper">
    <input
      type="number"
      min="0"
      max="100"
      step="0.5"
      className={`commission-input ${
        updatingLockieReward === l.id ? "updating" : ""
      } ${successLockieReward === l.id ? "success" : ""}`}
      value={l.lockie_reward_percentage ?? ""}
      placeholder="0"
      onChange={(e) => {
        const val = e.target.value;
        setLocksmiths((prev) =>
          prev.map((item) =>
            item.id === l.id
              ? { ...item, lockie_reward_percentage: val === "" ? null : val }
              : item
          )
        );
      }}
      onBlur={(e) => handleLockieRewardChange(l.id, e.target.value)}
    />
    <span className="percent-symbol">%</span>
  </div>
</td>
                

                <td>
                  <button
                    className="approve-btn"
                    onClick={() => handleApprove(l.id)}
                    disabled={l.is_approved}
                  >
                    Approve
                  </button>
                  <button
                    className="remove-btn"
                    onClick={() => handleReject(l.id)}
                    disabled={!l.is_approved}
                  >
                    Reject
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDelete(l.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManageLocksmith;